import React, { useEffect, useMemo, useState } from "react";
import {
  fetchCountryList,
  fetchDeliveryAddressList,
} from "../../store/orders/action";
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
} from "../../components/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from "../../../src/components/ui/accordion";
import {
  ViewCart,
  EmptyCart,
  DeliveryAddressForm,
} from "../../components/postOrder";
import { toast } from "react-toastify";
import { Clock, Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ROUTES from "../../services/AppConstant";
import { post } from "../../services/ApiService";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import { API_CONSTANT } from "../../services/ApiConstant";
import { setBookOrders } from "../../store/orders/reducer";
import { S_TEXT, TEXT } from "../../constant/textConstants";

const CheckOutPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth, bookOrders } = useSelector((state) => state);
  const currentUserData = auth?.authUser;

  const [formMode, setFormMode] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipState, setShipState] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [shipPhoneNo, setShipPhoneNo] = useState("");
  const [shipPinCode, setShipPinCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressLine3, setAddressLine3] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [recipientName, setRecipientName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [editingAddress, setEditingAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [addingNewAddress, setAddingNewAddress] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [selectedShipCountry, setSelectedShipCountry] = useState("");
  const [showConformationMail, setShowConformationMail] = useState(false);

  useEffect(() => {
    if (!currentUserData?.IsLoggedIn) {
      navigate(ROUTES?.LOGIN, { state: { fromScreen: ROUTES.CHECK_OUT } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (bookOrders?.deliveryAddressList?.AddressList?.length > 0) {
      setSelectedAddress(bookOrders?.deliveryAddressList?.AddressList[0]);
    }
  }, [bookOrders?.deliveryAddressList]);

  useEffect(() => {
    if (currentUserData?.IsLoggedIn && bookOrders?.countryList?.length === 0) {
      fetchCountryList(dispatch, auth);
    }
    if (
      currentUserData?.IsLoggedIn &&
      bookOrders?.deliveryAddressList?.length === 0
    ) {
    }
    if (currentUserData?.IsLoggedIn && bookOrders?.bookOrders?.length > 0) {
      fetchDeliveryAddressList(dispatch, auth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleFileChange = (event) => {
    if (event.target.files) {
      setAttachedFile(event.target.files[0]);
    }
  };

  const removeItem = (isbn13) => {
    dispatch(
      setBookOrders(
        bookOrders?.bookOrders?.filter((item) => item.ISBN13 !== isbn13),
      ),
    );
  };

  const createNewOrder = () => {
    setIsCreatingOrder(true);
    const orders = bookOrders?.bookOrders?.map((item) => {
      const [price, currency] = (item.Price ?? "").split(" ");
      return {
        SkuNumber: item?.SkuNumber,
        Qty: item?.quantity,
        Price: price,
        Currency: currency,
      };
    });

    const formData = new FormData();
    formData.append("AppUserId", auth?.authUser?.AppUserId);
    formData.append("OrderNumber", referenceNumber);
    formData.append("OrderList", JSON.stringify(orders));
    formData.append("DeliveryLabelFilePath", attachedFile);
    formData.append("ShipPhoneNo", selectedAddress?.ShipPhoneNo);
    formData.append("ShipPinCode", selectedAddress?.ShipPinCode);
    formData.append("ShipCityName", selectedAddress?.ShipCityName);
    formData.append("EmailAddress", selectedAddress?.EmailAddress);
    formData.append("ShipStateName", selectedAddress?.ShipStateName);
    formData.append("RecipientName", selectedAddress?.RecipientName);
    formData.append("ShipCountryId", selectedAddress?.ShipCountryId);
    formData.append("ShipAddressLine1", selectedAddress?.ShipAddressLine1);
    formData.append("ShipAddressLine2", selectedAddress?.ShipAddressLine2);
    formData.append("ShipAddressLine3", selectedAddress?.ShipAddressLine3);

    post(API_CONSTANT?.POST_NEW_ORDER, formData)
      .then((res) => {
        if (res?.data?.IsOrderPlaced) {
          toast.success(res?.data?.Message);
          navigate(ROUTES?.ORDER_LIST);
          dispatch(setBookOrders([]));
        }
      })
      .catch((err) => {
        toast.error(err?.data?.ErrorMessage || err?.message || String(err));
      })
      .finally(() => setIsCreatingOrder(false));
  };

  const addUpdateAddress = () => {
    setIsLoading(true);
    const obj = {
      AppUserId: auth?.authUser?.AppUserId,
      ShipCityName: shipCity,
      ShipStateName: shipState,
      ShipPinCode: shipPinCode,
      ShipPhoneNo: shipPhoneNo,
      EmailAddress: emailAddress,
      RecipientName: recipientName,
      ShipAddressLine1: addressLine1,
      ShipAddressLine2: addressLine2,
      ShipAddressLine3: addressLine3,
      CustomerId: selectedAddress?.CustomerId || 1,
      ShipCountryId: selectedShipCountry?.CountryId,
      DeliveryAddressId: selectedAddress
        ? selectedAddress?.DeliveryAddressId
        : 0,
      StatusId: selectedAddress
        ? selectedAddress?.StatusId
        : selectedStatus?.StatusId,
    };

    post(API_CONSTANT?.ADD_UPDATE_DELIVERY_ADDRESS, obj)
      .then((res) => {
        if (res?.data?.IsError) {
          toast.error(res?.data?.ErrorMessage);
        }
        if (res?.data?.IsProcessed) {
          setEditingAddress(false);
          setAddingNewAddress(false);
          setShowConformationMail(true);
          toast.success(res?.data?.ProcessMessage);
          fetchDeliveryAddressList(dispatch, auth);
        }
      })
      .catch((err) => {
        toast.error(err?.data?.ErrorMessage || err?.message || String(err));
      })
      .finally(() => setIsLoading(false));
  };

  const handleQuantityChange = (book, delta) => {
    const updatedCart = bookOrders?.bookOrders?.map((item) => {
      if (item.ISBN10 === book.ISBN10) {
        let newQuantity;

        if (delta === -1) {
          newQuantity = Math.max(item.quantity - 1, 0);
        } else {
          newQuantity = item.quantity + delta;
        }

        return { ...item, quantity: newQuantity };
      }
      return item;
    });

    dispatch(setBookOrders(updatedCart?.filter((item) => item?.quantity > 0)));
  };

  const handleSelectCountry = (selectedValue) => {
    const selectedCountry = bookOrders?.countryList?.find(
      (item) => item?.CountryId === selectedValue?.CountryId,
    );

    setSelectedShipCountry(selectedCountry);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateTotal = () => {
    return bookOrders?.bookOrders?.reduce((total, item) => {
      const price = parseFloat(item.Price.split(" ")[0]);
      return total + price * item.quantity;
    }, 0);
  };

  const grandTotalWithTax = () => {
    const subtotal = calculateTotal().toFixed(2);

    const shipping = currentUserData?.ShippingFee || 0;
    const vatRate = currentUserData?.VATinPercent || 0;

    const total = Number(subtotal) + shipping;
    return total * (1 + vatRate / 100);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const totalAmount = useMemo(() => calculateTotal().toFixed(2), [bookOrders]);
  const grandTotal = useMemo(
    () => grandTotalWithTax().toFixed(2),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookOrders, currentUserData],
  );

  const taxesTotal = useMemo(() => {
    return (
      ((calculateTotal().toFixed(2) + (currentUserData?.ShippingFee || 0)) *
        (currentUserData?.VATinPercent || 0)) /
      100
    );
  }, [
    calculateTotal,
    currentUserData?.ShippingFee,
    currentUserData?.VATinPercent,
  ]);

  return (
    <div className="container mx-auto max-w-[70%] !py-6 px-0 text-left sm:p-4">
      <>
        <div className="flex items-center justify-between gap-3">
          <h1 className="mb-6 text-center text-3xl font-bold">
            {t(TEXT?.CHECKOUT)}
          </h1>
          <div
            onClick={() => navigate(ROUTES?.ORDER_LIST)}
            className="group flex cursor-pointer items-center gap-[6px] text-gray-400"
          >
            <Clock className="h-5 w-5 group-hover:text-black-1" />
            <p className="text-center group-hover:text-black-1">
              {t(TEXT?.VIEW_PAST_ORDERS)}
            </p>
          </div>
        </div>

        {bookOrders?.bookOrders?.length !== 0 ? (
          <div className="flex h-auto flex-wrap items-start justify-between gap-5 md:flex-nowrap md:divide-x md:divide-dashed">
            {/* Left Section */}
            <Accordion
              type="multiple"
              className="w-full space-y-3 md:w-3/5"
              defaultValue={["summary", "address"]}
            >
              <AccordionItem value="summary" className="rounded-lg border">
                <AccordionTrigger
                  showArrow={false}
                  className="px-4 hover:no-underline"
                >
                  {t(TEXT?.ORDER_SUMMARY)}
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <div className="mt-4 rounded-lg border bg-white p-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="referenceNumber"
                          className="mb-[2px] block text-sm font-medium"
                        >
                          {t(TEXT?.YOUR)} {t(TEXT?.REFERENCE_NUMBER)} (
                          {t(TEXT?.OPTIONAL)})
                        </label>
                        <Input
                          id="referenceNumber"
                          value={referenceNumber}
                          placeholder={`${t(TEXT?.ENTER)} ${t(TEXT?.REFERENCE_NUMBER)}`}
                          className="w-full rounded-md border-gray-300"
                          onChange={(e) => setReferenceNumber(e.target.value)}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="fileAttachment"
                          className="mb-[2px] block text-sm font-medium"
                        >
                          {t(TEXT?.ATTACH_DOCUMENT)} ({t(TEXT?.OPTIONAL)})
                        </label>
                        <Input
                          type="file"
                          id="fileAttachment"
                          onChange={handleFileChange}
                          className="w-full rounded-md border-gray-300"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-full py-2">
                    <ViewCart
                      removeItem={removeItem}
                      handleQuantityChange={handleQuantityChange}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="address" className="rounded-lg border">
                <AccordionTrigger
                  showArrow={false}
                  className="px-4 hover:no-underline"
                >
                  {t(TEXT?.DELIVERY_ADDRESS).toUpperCase()}
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <div className="w-full py-2">
                    {!editingAddress && !addingNewAddress ? (
                      <>
                        <div>
                          {bookOrders?.deliveryAddressList?.AddressList?.slice(
                            0,
                            showAllAddresses ? undefined : 3,
                          )?.map((address) => {
                            return (
                              <div
                                key={address?.DeliveryAddressId}
                                onClick={() => setSelectedAddress(address)}
                                className="flex cursor-pointer items-start space-x-2 py-2"
                              >
                                <div className="mt-[2px]">
                                  <input
                                    type="radio"
                                    name="deliveryAddress"
                                    id={address?.DeliveryAddressId}
                                    value={address?.DeliveryAddressId}
                                    checked={
                                      selectedAddress?.DeliveryAddressId ===
                                      address?.DeliveryAddressId
                                    }
                                    onChange={() => setSelectedAddress(address)}
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start gap-4">
                                    <div>{address.RecipientName}</div>
                                    <div>{address.ShipPhoneNo}</div>
                                  </div>

                                  <div className="mt-1 text-sm text-muted-foreground">
                                    {[
                                      address.ShipAddressLine1,
                                      address.ShipAddressLine2,
                                      address.ShipAddressLine3,
                                      address.ShipCityName,
                                      address.ShipStateName,
                                      address.ShipPinCode,
                                      address.ShipCountryId,
                                    ]
                                      .filter((part) => part)
                                      .join(", ")}
                                  </div>
                                </div>
                                {selectedAddress?.DeliveryAddressId ===
                                  address?.DeliveryAddressId && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="ml-auto"
                                    onClick={() => {
                                      setFormMode("edit");
                                      setEditingAddress(
                                        address?.DeliveryAddressId,
                                      );
                                    }}
                                  >
                                    {t(TEXT?.EDIT)}
                                  </Button>
                                )}
                              </div>
                            );
                          })}

                          {bookOrders?.deliveryAddressList?.AddressList
                            ?.length > 3 && (
                            <Button
                              variant="link"
                              className="mt-2 text-blue-600"
                              onClick={() =>
                                setShowAllAddresses(!showAllAddresses)
                              }
                            >
                              {showAllAddresses
                                ? `${t(TEXT?.SHOW_LESS)}`
                                : `${t(TEXT?.VIEW_ALL)} ${bookOrders?.deliveryAddressList?.AddressList?.length} ${t(TEXT?.ADDRESSES)}`}
                            </Button>
                          )}
                        </div>

                        <div
                          onClick={() => {
                            setFormMode("add");
                            setAddingNewAddress(true);
                          }}
                          className="cursor-pointer border-y-[1px] px-2 py-3"
                        >
                          <p className="text-sm font-semibold transition-colors hover:text-blue-400">
                            + {t(TEXT?.ADD_NEW_ADDRESS)}
                          </p>
                        </div>
                      </>
                    ) : (
                      <DeliveryAddressForm
                        formMode={formMode}
                        shipCity={shipCity}
                        isLoading={isLoading}
                        shipState={shipState}
                        bookOrders={bookOrders}
                        shipPhoneNo={shipPhoneNo}
                        shipPinCode={shipPinCode}
                        setShipCity={setShipCity}
                        emailAddress={emailAddress}
                        addressLine1={addressLine1}
                        addressLine2={addressLine2}
                        addressLine3={addressLine3}
                        setShipState={setShipState}
                        attachedFile={attachedFile}
                        recipientName={recipientName}
                        selectedStatus={selectedStatus}
                        setShipPhoneNo={setShipPhoneNo}
                        setShipPinCode={setShipPinCode}
                        referenceNumber={referenceNumber}
                        setEmailAddress={setEmailAddress}
                        selectedAddress={selectedAddress}
                        setAddressLine1={setAddressLine1}
                        setAddressLine2={setAddressLine2}
                        setAddressLine3={setAddressLine3}
                        addUpdateAddress={addUpdateAddress}
                        setRecipientName={setRecipientName}
                        handleFileChange={handleFileChange}
                        setSelectedStatus={setSelectedStatus}
                        setEditingAddress={setEditingAddress}
                        setReferenceNumber={setReferenceNumber}
                        setSelectedAddress={setSelectedAddress}
                        selectedShipCountry={selectedShipCountry}
                        handleSelectCountry={handleSelectCountry}
                        setAddingNewAddress={setAddingNewAddress}
                        showConformationMail={showConformationMail}
                        setSelectedShipCountry={setSelectedShipCountry}
                        setShowConformationMail={setShowConformationMail}
                      />
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="flex w-full flex-col items-center gap-5 md:w-2/5 md:px-5">
              <Card className="w-full">
                <CardHeader className="px-3 pb-1 pt-3">
                  <CardTitle className="text-base font-medium text-gray-1">
                    {t(TEXT?.PRICE).toUpperCase()}
                    <span className="uppercase"> {t(TEXT?.DETAILS)}</span>
                  </CardTitle>
                </CardHeader>
                <div className="h-px w-full bg-border" />
                <CardContent className="space-y-4 !p-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm lg:text-base">
                        {t(TEXT?.PRICE)} ({bookOrders?.bookOrders?.length}{" "}
                        {t(TEXT?.ITEMS)})
                      </span>
                      <span className="min-w-max text-sm font-semibold text-black-1 lg:text-base">
                        {totalAmount}{" "}
                        {currentUserData?.CustomerCurrency || S_TEXT?.USD}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm lg:text-base">
                        {t(TEXT?.DELIVERY_CHARGES)}
                      </span>
                      <div className="flex items-center gap-2">
                        {currentUserData?.ShippingFee ? (
                          <span className="text-muted-foreground">
                            {currentUserData.ShippingFee}{" "}
                            {currentUserData.CustomerCurrency || S_TEXT?.USD}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    {taxesTotal > 0 && (
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-sm lg:text-base">
                          {t(TEXT?.TAXES)}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">
                            {taxesTotal}{" "}
                            {currentUserData.CustomerCurrency || S_TEXT?.USD}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="h-px w-full border-t border-dashed border-border" />

                  <div className="flex items-center justify-between gap-1">
                    <span className="text-sm font-medium lg:text-base">
                      {t(TEXT?.TOTAL_PAYABLE)}
                    </span>
                    <span className="text-base font-semibold text-black-1">
                      {grandTotal}{" "}
                      {currentUserData?.CustomerCurrency || S_TEXT?.USD}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <div className="flex w-full items-center justify-end">
                <Button
                  disabled={isCreatingOrder}
                  onClick={() => createNewOrder()}
                  className="min-w-[150px] bg-purple-1 text-base text-white hover:bg-gray-200 hover:font-medium hover:text-purple-1 disabled:cursor-not-allowed disabled:opacity-75"
                >
                  {isCreatingOrder ? (
                    <>
                      <Loader className="mr-2 h-5 w-5 animate-spin" />
                      {t(TEXT?.PROCESSING)}
                    </>
                  ) : (
                    t(TEXT?.PLACE_ORDER)
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <EmptyCart />
        )}
      </>
    </div>
  );
};

export default CheckOutPage;
